<template>
  <VaSidebar
    v-model="writableVisible"
    class="md:!my-4 md:!ml-2 md:h-auto"
    :class="[props.mobile ? '!w-full h-[76vh] !shadow-none' : 'h-screen']"
    :minimized="minimized"
    :width="sidebarWidth"
    :color="color"
    :minimized-width="sidebarMinimizedWidth"
  >
    <h2
      class="header"
      :class="{ 'header-minimized': minimized }"
      @click="(props.mobile || props.tablet) && (isSidebarMinimized = !isSidebarMinimized)"
    >
      <template v-if="minimized">
        <!-- <span class="block text-sm sm:text-base md:text-md whitespace-nowrap"> FSA </span> -->
        <span class="pr-2 pl-2">
          <VaIcon name="menu" class="lg:hidden cursor-pointer" size="20px" />
        </span>
      </template>
      <template v-else>
        Fast and<br />
        Secure Access
      </template>
    </h2>
    <VaAccordion v-model="value" multiple>
      <VaCollapse v-for="(route, index) in permittedRoutes" :key="index">
        <template #header="{ value: isCollapsed }">
          <VaSidebarItem
            :to="route.children ? undefined : { name: route.name }"
            :active="routeHasActiveChild(route)"
            :active-color="activeColor"
            :text-color="textColor(route)"
            :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
            role="button"
            hover-opacity="0.10"
            @click="
              isSidebarMinimized = false;
              $emit('update:menuItemClicked');
            "
          >
            <VaSidebarItemContent class="py-3 pr-2 pl-4">
              <VaIcon v-if="route.meta.icon" aria-hidden="true" :name="route.meta.icon" size="20px" />
              <VaSidebarItemTitle
                v-if="minimized === false"
                class="flex justify-between items-center leading-5 font-semibold"
              >
                {{ t(route.displayName) }}
                <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
              </VaSidebarItemTitle>
            </VaSidebarItemContent>
          </VaSidebarItem>
        </template>
        <template #body>
          <div v-for="(childRoute, index2) in route.children" :key="index2">
            <VaSidebarItem
              :to="{ name: childRoute.name }"
              :active="isActiveChildRoute(childRoute)"
              :active-color="activeColor"
              :class="{ hidden: minimized, 'h-0': minimized }"
              :text-color="textColor(childRoute)"
              :aria-label="`Visit ${t(route.displayName)}`"
              hover-opacity="0.10"
              @click="
                isSidebarMinimized = false;
                $emit('update:menuItemClicked');
              "
            >
              <VaSidebarItemContent class="py-3 pr-2 pl-11">
                <VaSidebarItemTitle class="leading-5 font-semibold">
                  {{ t(childRoute.displayName) }}
                </VaSidebarItemTitle>
              </VaSidebarItemContent>
            </VaSidebarItem>
          </div>
        </template>
      </VaCollapse>
    </VaAccordion>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';
import { useColors } from 'vuestic-ui';

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes';
import { storeToRefs } from 'pinia';
import { useGlobalStore } from '../../stores/global-store';
import { useUserStore } from '@stores/user-store';

export default defineComponent({
  name: 'Sidebar',
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
    tablet: { type: Boolean, default: false },
    minimized: { type: Boolean, default: false },
  },
  emits: ['update:visible', 'update:menuItemClicked'],

  setup: (props, { emit }) => {
    const { getColor, colorToRgba } = useColors();
    const route = useRoute();
    const { t } = useI18n();
    const { isSidebarMinimized } = storeToRefs(useGlobalStore());

    const value = ref<boolean[]>([]);

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    });

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name;

    const routeHasActiveChild = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(`${section.name}`);
      }

      return section.children.some(({ name }) => route.path.endsWith(`${name}`));
    };

    const userStore = useUserStore();
    const router = useRouter();

    const permittedRoutes = computed(() => {
      let processedMenu = navigationRoutes.routes.map((route) => {
        const { href: resolvedPath } = router.resolve({ name: route.name });
        if (userStore.pagePermissions.indexOf(resolvedPath) > -1 || resolvedPath.indexOf('auth') > -1) {
          return route;
        } else {
          return null;
        }
      });

      processedMenu = processedMenu.filter((item) => item !== null);
      return [...processedMenu] as INavigationRoute[];
    });

    const isActiveRoute = (section: INavigationRoute) => {
      if (!section.children) {
        return route.path.endsWith(section.name);
      }
    };

    const sidebarWidth = computed(() => (props.mobile ? '50vw' : '250px'));
    const sidebarMinimizedWidth = computed(() => (props.mobile ? '0px' : '52px'));
    // const sidebarMinimizedWidth = '52px';
    const color = computed(() => getColor('background-secondary'));
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1));

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary');
    const textColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'textPrimary');
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down');

    const setActiveExpand = () =>
      (value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route)));

    watch(() => route.fullPath, setActiveExpand, { immediate: true });

    return {
      writableVisible,
      sidebarWidth,
      sidebarMinimizedWidth,
      value,
      color,
      activeColor,
      permittedRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      isActiveRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      props,
      isSidebarMinimized,
    };
  },
});
</script>

<style scoped>
.header-minimized {
  @apply pl-2 pr-2;
}
</style>
